import React, { useCallback, useEffect, useMemo, useState } from "react";
import { message as antdMessage } from "antd";
import {
  getFilterUids, getRuleSets, saveRuleSet,
} from "api/tracking/trackingElementsApi";
import { expandCompactUids } from "webgl/js/loader/streaming/scopeFilter";
import {
  GATES, MAX_RULES, MEASURE_FIELDS, STATUSES, TEXT_FIELDS, VALUELESS_OPS,
  blankRule, fieldKind, opsFor, resolveFilter, rulesProblem,
} from "./filterBarModel";
import "./ModelFilterBar.scss";

// THE FILTER BAR FOR THE MODEL VIEW.
//
// One vocabulary, one evaluator, one store, and none of the three is new:
//   vocabulary  back/utils/quantityFilters.js - the construction tracking
//               rules, which is what the owner chose.
//   evaluator   the SERVER, POST /quantities/filter-uids. The browser never
//               decides what matches; it asks and draws the answer. That is
//               also what keeps the lazy record tree out of it - the match
//               runs over the whole live commit, not over the nodes that
//               happen to be expanded.
//   store       TrackingRuleSet, the company's saved rule sets. The tracking
//               page and this bar load the SAME ROW, so a filter saved in
//               one is the filter in the other - not two tables agreeing.
//
// The bar is a CONTAINER. Its controls are an ordered list and adding the
// next one is an entry in it, not a re-layout.
// Not an id: the last option in the Load list opens the Saved Filters page.
const SEE_ALL = "__see_all_saved_filters";

const ModelFilterBar = ({ projectId, onIsolate, onShowEverything, onClose }) => {
  const [rules, setRules] = useState([blankRule()]);
  const [gate, setGate] = useState("AND");
  const [busy, setBusy] = useState(false);
  const [answer, setAnswer] = useState(null);
  const [sets, setSets] = useState([]);
  const [saveName, setSaveName] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let dead = false;
    getRuleSets(projectId)
      .then((res) => { if (!dead) setSets((res && (res.sets || res)) || []); })
      // A rule-set list that cannot be read is not a reason to break the bar:
      // filtering still works, only loading a saved one does not.
      .catch(() => { if (!dead) setSets([]); });
    return () => { dead = true; };
  }, [projectId]);

  const problem = useMemo(() => rulesProblem(rules), [rules]);

  const patch = (i, next) => setRules((rs) => rs.map((r, n) => (n === i ? { ...r, ...next } : r)));
  const addRule = () => setRules((rs) => (rs.length >= MAX_RULES ? rs : [...rs, blankRule()]));
  const dropRule = (i) => setRules((rs) => (rs.length === 1 ? [blankRule()] : rs.filter((r, n) => n !== i)));

  const apply = useCallback(async () => {
    if (problem) { antdMessage.warning(problem); return; }
    setBusy(true);
    try {
      // ONE CALL FOR BOTH HALVES. resolveFilter returns the uid set and the
      // sentence together and cannot return one without the other, so the
      // state the owner rejected - elements hidden with no count saying how
      // many are outside the tracked scope - is unreachable from here rather
      // than merely avoided. Do not unpick this into two steps.
      const res = await resolveFilter({
        fetchUids: getFilterUids,
        expand: expandCompactUids,
        projectId, rules, gate,
      });
      setAnswer({ kind: res.kind, message: res.message, count: res.uids ? res.uids.length : 0 });
      // ONLY AN 'ok' ANSWER CHANGES THE MODEL. Refused, timed out and empty
      // all leave the view exactly as it was and say so - hiding everything
      // because the server refused would look like a filter that worked.
      if (res.kind === "ok" && res.uids && res.uids.length) onIsolate(res.uids);
    } catch (e) {
      setAnswer({ kind: "error", message: (e && e.message) || "The filter could not be run" });
    } finally {
      setBusy(false);
    }
  }, [projectId, rules, gate, problem, onIsolate]);

  const reset = useCallback(() => {
    setAnswer(null);
    onShowEverything();
  }, [onShowEverything]);

  const save = useCallback(async (replace = false) => {
    const name = saveName.trim();
    if (!name) { antdMessage.warning("Give the filter a name"); return; }
    if (problem) { antdMessage.warning(problem); return; }
    setSaving(true);
    try {
      const res = await saveRuleSet(projectId, { name, rules, gate, replace });
      setSets((s) => {
        const saved = res && res.set;
        if (!saved) return s;
        return [saved, ...s.filter((x) => String(x._id) !== String(saved._id))];
      });
      antdMessage.success(res && res.replaced ? `"${name}" replaced` : `Saved as "${name}"`);
      setSaveName("");
    } catch (e) {
      // The server answers 409 with the set that already has the name. Saving
      // over somebody else's work is a choice, so it is asked for, never done
      // because a name happened to collide.
      const msg = (e && e.message) || "Could not save";
      if (/already has rules called/i.test(msg)) {
        antdMessage.warning(`${msg} - press Save again to replace it`);
        setSaving(false);
        // eslint-disable-next-line consistent-return
        return { needsReplace: true };
      }
      antdMessage.error(msg);
    } finally {
      setSaving(false);
    }
  }, [projectId, rules, gate, saveName, problem]);

  const load = (set) => {
    if (!set) return;
    setRules((set.rules && set.rules.length ? set.rules : [blankRule()]));
    setGate(set.gate || "AND");
    setAnswer(null);
    antdMessage.info(`Loaded "${set.name}"`);
  };

  const ruleRow = (rule, i) => {
    const kind = fieldKind(rule.field);
    return (
      <div className="mfb-rule" key={i}>
        <select value={rule.field} onChange={(e) => patch(i, { field: e.target.value, op: opsFor(e.target.value)[0], value: "" })}>
          <optgroup label="Text">
            {TEXT_FIELDS.map((f) => <option key={f} value={f}>{f}</option>)}
          </optgroup>
          <optgroup label="Status"><option value="status">status</option></optgroup>
          <optgroup label="Measure">
            {MEASURE_FIELDS.map((f) => <option key={f} value={f}>{f}</option>)}
          </optgroup>
        </select>
        <select value={rule.op} onChange={(e) => patch(i, { op: e.target.value })}>
          {opsFor(rule.field).map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
        {VALUELESS_OPS.includes(rule.op) ? (
          <span className="mfb-novalue">(no value needed)</span>
        ) : kind === "status" ? (
          <select value={rule.value} onChange={(e) => patch(i, { value: e.target.value })}>
            <option value="">choose…</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
          </select>
        ) : (
          <input
            type={kind === "number" ? "number" : "text"}
            value={rule.value}
            placeholder={kind === "number" ? "0" : "value"}
            onChange={(e) => patch(i, { value: e.target.value })}
          />
        )}
        <button type="button" className="mfb-x" onClick={() => dropRule(i)} title="Remove this rule">×</button>
      </div>
    );
  };

  // THE ORDERED LIST OF CONTROLS. The next control is an entry here.
  const CONTROLS = [
    {
      key: "gate",
      render: () => (
        <label className="mfb-control mfb-gate">
          <span>Match</span>
          <select value={gate} onChange={(e) => setGate(e.target.value)}>
            {GATES.map((g) => <option key={g.value} value={g.value} title={g.label}>{g.value}</option>)}
          </select>
        </label>
      ),
    },
    {
      key: "rules",
      grow: true,
      render: () => (
        <div className="mfb-control mfb-rules">
          {rules.map(ruleRow)}
          <button type="button" className="mfb-add" onClick={addRule} disabled={rules.length >= MAX_RULES}>
            + rule
          </button>
        </div>
      ),
    },
    {
      key: "count",
      render: () => (
        <div className={`mfb-control mfb-count mfb-${answer ? answer.kind : "idle"}`}>
          {busy ? "counting…" : (answer ? answer.message : "no filter applied")}
        </div>
      ),
    },
    {
      key: "apply",
      render: () => (
        <div className="mfb-control mfb-actions">
          <button type="button" className="mfb-apply" onClick={apply} disabled={busy || !!problem} title={problem || "Show only what matches"}>
            Apply
          </button>
          <button type="button" onClick={reset} disabled={busy}>Reset</button>
        </div>
      ),
    },
    {
      key: "saveload",
      render: () => (
        <div className="mfb-control mfb-saveload">
          {/* The way to the full list lives IN this select, not beside it.
              Measured on the page: the bar has 65px spare at both 1600 and
              1400, and a link is about 90 - a fourth control would wrap the
              bar onto a second line, which is a regression this bar has had
              once already. An extra <option> costs no width at all.
              The select is also no longer disabled when empty: no saved
              filters is exactly when somebody most needs to be told the
              list exists. */}
          <select
            value=""
            onChange={(e) => {
              if (e.target.value === SEE_ALL) {
                window.open("/filter-view", "_blank", "noopener");
                return;
              }
              load(sets.find((s) => String(s._id) === e.target.value));
            }}
            title={sets.length ? "Load a saved filter" : "Your company has no saved filters yet"}
          >
            <option value="">{sets.length ? "Load…" : "No saved filters"}</option>
            {sets.map((s) => <option key={s._id} value={s._id}>{s.name}</option>)}
            <option value={SEE_ALL}>See all saved filters…</option>
          </select>
          <input
            type="text"
            value={saveName}
            placeholder="Save as…"
            onChange={(e) => setSaveName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") save(false); }}
          />
          <button type="button" onClick={() => save(false)} disabled={saving || !!problem}>Save</button>
        </div>
      ),
    },
  ];

  return (
    <div className="model-filter-bar" role="region" aria-label="Model filter">
      {CONTROLS.map((c) => (
        <div className={`mfb-slot${c.grow ? " mfb-grow" : ""}`} key={c.key}>{c.render()}</div>
      ))}
      {onClose && (
        <button type="button" className="mfb-close" onClick={onClose} title="Close the filter bar">×</button>
      )}
    </div>
  );
};

export default ModelFilterBar;
